import React, { useState, useEffect } from 'react';

import api from "../../../../../../../services/api";

import {
    Badge,
    Button,
    ButtonGroup,
    Input,
    InputGroup,
    Spinner,
} from "reactstrap";
import classnames from "classnames";

const PAPEIS = [
    { id: 'operador', text: 'Recebe demandas' },
    { id: 'observador', text: 'Observador' },
];

/**
 * Gerencia os membros de um funil (marketing.membros_funis) — a mesma tabela
 * usada pelo novo CRM, então o que for cadastrado aqui vale nos dois sistemas.
 *
 * Um funil com membros ativos só aparece para esses membros; um funil sem
 * nenhum membro continua visível para todos da empresa.
 */
export default ({ notify, empresa, funilId, ...props }) => {

    const [membros, setMembros] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [equipes, setEquipes] = useState([]);
    const [equipeFiltro, setEquipeFiltro] = useState('');
    const [busca, setBusca] = useState('');
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        if (empresa?.id && funilId) {
            loadMembros();
            loadEquipes();
        }
    }, [empresa?.id, funilId])

    useEffect(() => {
        if (empresa?.id) loadColaboradores();
    }, [empresa?.id, equipeFiltro])

    async function loadMembros() {
        setLoading(true)
        try {
            const response = await api.get(
                `/common/empresas/${empresa.id}/funis/${funilId}/membros`
            );
            setMembros(response.data || []);
        } catch (error) {
            console.error(error);
            notify('danger', 'Não foi possível carregar os membros do funil');
        } finally {
            setLoading(false)
        }
    }

    async function loadColaboradores() {
        try {
            const params = { situacaoNaEmpresa: 'A' }
            if (equipeFiltro) params.equipes = [equipeFiltro]

            const response = await api.get(
                `/common/empresas/${empresa.id}/colaboradores`,
                { params }
            );
            setColaboradores(response.data || []);
        } catch (error) {
            console.error(error);
            notify('danger', 'Não foi possível carregar os colaboradores');
        }
    }

    async function loadEquipes() {
        try {
            const response = await api.get(`/common/empresas/${empresa.id}/equipes`);
            setEquipes(response.data || []);
        } catch (error) {
            console.error(error);
        }
    }

    async function adicionar(user_id) {
        setBusyId(user_id)
        try {
            await api.post(
                `/common/empresas/${empresa.id}/funis/${funilId}/membros`,
                { user_id }
            );
            setBusca('')
            await loadMembros();
        } catch (error) {
            console.error(error);
            notify('danger', error?.response?.data?.message || 'Não foi possível adicionar o membro');
        } finally {
            setBusyId(null)
        }
    }

    async function alterarPapel(user_id, papel) {
        // Otimista: reflete já e recarrega se der erro.
        setMembros((prev) => prev.map((m) => m.user_id === user_id ? { ...m, papel } : m))
        try {
            await api.put(
                `/common/empresas/${empresa.id}/funis/${funilId}/membros/${user_id}/papel`,
                { papel }
            );
        } catch (error) {
            console.error(error);
            notify('danger', error?.response?.data?.message || 'Não foi possível atualizar o papel');
            loadMembros();
        }
    }

    async function remover(user_id) {
        setBusyId(user_id)
        try {
            await api.delete(
                `/common/empresas/${empresa.id}/funis/${funilId}/membros/${user_id}`
            );
            setMembros((prev) => prev.filter((m) => m.user_id !== user_id))
        } catch (error) {
            console.error(error);
            notify('danger', error?.response?.data?.message || 'Não foi possível remover o membro');
        } finally {
            setBusyId(null)
        }
    }

    const idsMembros = membros.map((m) => m.user_id)
    const termo = busca.trim().toLowerCase()
    const disponiveis = colaboradores
        .filter((c) => !idsMembros.includes(c.id))
        .filter((c) => termo
            ? `${c.name || ''} ${c.email || ''}`.toLowerCase().includes(termo)
            : true)

    return (
        <>
            <p className="text-muted mb-3" style={{ fontSize: '0.8125rem' }}>
                Colaboradores que participam deste funil. <strong>Recebe demandas</strong>: entra
                na distribuição e pode ser responsável. <strong>Observador</strong>: apenas
                visualiza o funil. Enquanto o funil não tiver nenhum membro, ele continua
                visível para todos da empresa.
            </p>

            <h5 className="mb-2">
                Membros
                {membros.length > 0 && (
                    <Badge color="secondary" className="ml-2" pill>{membros.length}</Badge>
                )}
            </h5>

            {loading && membros.length === 0 ? (
                <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                </div>
            ) : membros.length === 0 ? (
                <p className="text-muted text-center py-4" style={{ fontSize: '0.8125rem' }}>
                    Nenhum membro ainda. Adicione colaboradores abaixo.
                </p>
            ) : (
                <div className="mb-4">
                    {membros.map((m) => (
                        <div
                            key={m.user_id}
                            className="d-flex align-items-center border rounded px-3 py-2 mb-2"
                        >
                            <div className="flex-grow-1 min-width-0">
                                <div className="text-truncate">{m.nome || m.email}</div>
                                {m.nome && (
                                    <small className="text-muted text-truncate d-block">{m.email}</small>
                                )}
                            </div>

                            <ButtonGroup size="sm" className="mr-2">
                                {PAPEIS.map((p) => (
                                    <Button
                                        key={p.id}
                                        color="secondary"
                                        className={classnames({ active: m.papel === p.id })}
                                        onClick={() => alterarPapel(m.user_id, p.id)}
                                    >
                                        {p.text}
                                    </Button>
                                ))}
                            </ButtonGroup>

                            <Button
                                color="link"
                                size="sm"
                                className="text-danger p-1"
                                title="Remover membro"
                                disabled={busyId === m.user_id}
                                onClick={() => remover(m.user_id)}
                            >
                                {busyId === m.user_id
                                    ? <Spinner size="sm" />
                                    : <i className="fas fa-times" />}
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <h5 className="mb-2">Adicionar colaborador</h5>

            <div className="d-flex mb-2">
                {equipes.length > 0 && (
                    <Input
                        type="select"
                        bsSize="sm"
                        className="mr-2"
                        style={{ maxWidth: 220 }}
                        value={equipeFiltro}
                        onChange={({ target }) => setEquipeFiltro(target.value)}
                    >
                        <option value="">Todas as equipes</option>
                        {equipes.map((e) => (
                            <option key={e.id} value={e.id}>{e.nome}</option>
                        ))}
                    </Input>
                )}
                <InputGroup size="sm">
                    <Input
                        placeholder="Buscar colaborador..."
                        value={busca}
                        onChange={({ target }) => setBusca(target.value)}
                    />
                </InputGroup>
            </div>

            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {disponiveis.length === 0 ? (
                    <p className="text-muted text-center py-3" style={{ fontSize: '0.8125rem' }}>
                        {colaboradores.length === 0
                            ? 'Nenhum colaborador ativo nesta empresa.'
                            : equipeFiltro
                                ? 'Nenhum colaborador disponível na equipe selecionada.'
                                : 'Todos os colaboradores já são membros.'}
                    </p>
                ) : (
                    disponiveis.map((c) => (
                        <div
                            key={c.id}
                            className="d-flex align-items-center px-2 py-1"
                        >
                            <div className="flex-grow-1 min-width-0">
                                <div className="text-truncate">{c.name || c.email}</div>
                                {c.name && (
                                    <small className="text-muted text-truncate d-block">{c.email}</small>
                                )}
                            </div>
                            <Button
                                color="primary"
                                size="sm"
                                outline
                                disabled={busyId === c.id}
                                onClick={() => adicionar(c.id)}
                            >
                                {busyId === c.id
                                    ? <Spinner size="sm" />
                                    : <i className="fas fa-plus" />}
                            </Button>
                        </div>
                    ))
                )}
            </div>
        </>
    );
}
